/**
 * React Query Hooks for Agents API
 * 
 * Provides data fetching, mutations, and caching for agent management.
 * All queries are scoped to the current tenant.
 */

import { useQuery, useMutation, useQueryClient, UseQueryOptions } from '@tanstack/react-query';
import apiClient, { ApiResponse, PaginatedResponse, getErrorMessage } from '../lib/api-client';
import { useTenant } from '@/contexts/TenantContext';
import { capabilityKeys } from '@/features/capabilities/hooks';

// =============================================================================
// Types
// =============================================================================

// Re-export types from the types module for convenience
export type { 
  Agent, 
  AgentStatus, 
  AgentCreate, 
  AgentUpdate, 
  AgentSummary,
  ModelsConfig,
  ModelConfig,
  PromptsConfig,
  KnowledgeConfig,
  ToolConfig,
  AgentSettings,
} from '@/features/agents/types';

// Backwards compatibility aliases
export type { AgentCreate as CreateAgentInput } from '@/features/agents/types';
export type { AgentUpdate as UpdateAgentInput } from '@/features/agents/types';

import type {
  Agent,
  AgentCreate,
  AgentUpdate,
  AgentStatus,
  RestrictedAgent,
} from '@/features/agents/types';

export interface AgentListParams {
  page?: number;
  page_size?: number;
  status?: AgentStatus;
  search?: string;
  sort_by?: 'name' | 'created_at' | 'updated_at';
  sort_order?: 'asc' | 'desc';
}

// =============================================================================
// Query Keys
// =============================================================================

export const agentKeys = {
  all: (tenantId: string | undefined) => ['agents', tenantId] as const,
  lists: (tenantId: string | undefined) => [...agentKeys.all(tenantId), 'list'] as const,
  list: (tenantId: string | undefined, params: AgentListParams) => [...agentKeys.lists(tenantId), params] as const,
  details: (tenantId: string | undefined) => [...agentKeys.all(tenantId), 'detail'] as const,
  detail: (tenantId: string | undefined, id: string) => [...agentKeys.details(tenantId), id] as const,
};

// =============================================================================
// API Functions
// =============================================================================

async function fetchAgents(params: AgentListParams = {}, tenantId: string): Promise<PaginatedResponse<Agent>> {
  const { data } = await apiClient.get<PaginatedResponse<Agent>>(
    `/api/v1/tenants/${tenantId}/agents`,
    { params },
  );
  return data;
}

async function fetchAgent(id: string, tenantId: string): Promise<Agent> {
  const { data } = await apiClient.get<ApiResponse<Agent>>(
    `/api/v1/tenants/${tenantId}/agents/${id}`,
  );
  return data.data;
}

async function createAgent(input: AgentCreate, tenantId: string): Promise<Agent> {
  const { data } = await apiClient.post<ApiResponse<Agent>>(
    `/api/v1/tenants/${tenantId}/agents`,
    input,
  );
  return data.data;
}

async function updateAgent(id: string, input: AgentUpdate, tenantId: string): Promise<Agent> {
  const { data } = await apiClient.patch<ApiResponse<Agent>>(
    `/api/v1/tenants/${tenantId}/agents/${id}`,
    input,
  );
  return data.data;
}

async function deleteAgent(id: string, tenantId: string): Promise<void> {
  await apiClient.delete(`/api/v1/tenants/${tenantId}/agents/${id}`);
}

async function activateAgent(id: string, tenantId: string): Promise<Agent> {
  const { data } = await apiClient.post<ApiResponse<Agent>>(
    `/api/v1/tenants/${tenantId}/agents/${id}/activate`,
  );
  return data.data;
}

async function archiveAgent(id: string, tenantId: string): Promise<Agent> {
  const { data } = await apiClient.post<ApiResponse<Agent>>(
    `/api/v1/tenants/${tenantId}/agents/${id}/archive`,
  );
  return data.data;
}

async function deprecateAgent(id: string, tenantId: string): Promise<Agent> {
  const { data } = await apiClient.post<ApiResponse<Agent>>(
    `/api/v1/tenants/${tenantId}/agents/${id}/deprecate`,
  );
  return data.data;
}

async function duplicateAgent(id: string, tenantId: string): Promise<Agent> {
  const { data } = await apiClient.post<ApiResponse<Agent>>(
    `/api/v1/tenants/${tenantId}/agents/${id}/duplicate`,
  );
  return data.data;
}

// =============================================================================
// Query Hooks
// =============================================================================

/**
 * List agents with pagination and filtering
 * Only runs when a tenant is selected.
 */
export function useAgents(
  params: AgentListParams = {},
  options?: Omit<UseQueryOptions<PaginatedResponse<Agent>>, 'queryKey' | 'queryFn'>
) {
  const { currentTenant } = useTenant();
  const tenantId = currentTenant?.id;
  const { enabled: enabledOpt = true, ...restOptions } = options ?? {};

  return useQuery({
    queryKey: agentKeys.list(tenantId, params),
    queryFn: () => fetchAgents(params, tenantId as string),
    enabled: !!tenantId && enabledOpt, // Tenant is mandatory — caller options can't bypass it
    staleTime: 30 * 1000, // 30 seconds
    ...restOptions,
  });
}

/**
 * Get single agent by ID
 * Only runs when a tenant is selected.
 *
 * Sprint 219 — REQ-AGT-003: `use_only` agents return the restricted
 * representation from the API (identity only).
 */
export function useAgent(
  id: string,
  options?: Omit<UseQueryOptions<Agent | RestrictedAgent>, 'queryKey' | 'queryFn'>
) {
  const { currentTenant } = useTenant();
  const tenantId = currentTenant?.id;
  const { enabled: enabledOpt = true, ...restOptions } = options ?? {};

  return useQuery({
    queryKey: agentKeys.detail(tenantId, id),
    queryFn: () => fetchAgent(id, tenantId as string),
    enabled: !!id && !!tenantId && enabledOpt, // Tenant is mandatory — caller options can't bypass it
    staleTime: 60 * 1000, // 1 minute
    ...restOptions,
  });
}

// =============================================================================
// Mutation Hooks
// =============================================================================

/**
 * Create a new agent
 */
export function useCreateAgent() {
  const queryClient = useQueryClient();
  const { currentTenant } = useTenant();
  const tenantId = currentTenant?.id;
  
  return useMutation({
    mutationFn: (input: AgentCreate) => createAgent(input, tenantId as string),
    onSuccess: (newAgent) => {
      // Invalidate list queries
      queryClient.invalidateQueries({ queryKey: agentKeys.lists(tenantId) });
      
      // Pre-populate the detail cache
      queryClient.setQueryData(agentKeys.detail(tenantId, newAgent.id), newAgent);
    },
    onError: (error) => {
      console.error('Failed to create agent:', getErrorMessage(error));
    },
  });
}

/**
 * Update an existing agent
 */
export function useUpdateAgent() {
  const queryClient = useQueryClient();
  const { currentTenant } = useTenant();
  const tenantId = currentTenant?.id;
  
  return useMutation({
    mutationFn: ({ id, ...input }: AgentUpdate & { id: string }) =>
      updateAgent(id, input, tenantId as string),
    onSuccess: (updatedAgent) => {
      // ISS-599: el PATCH puede cambiar la decisión explícita de capacidades del
      // agente — refrescar el slice de capabilities (decisión + resolución).
      queryClient.invalidateQueries({ queryKey: capabilityKeys.all });

      // Update the detail cache
      queryClient.setQueryData(agentKeys.detail(tenantId, updatedAgent.id), updatedAgent);
      
      // Invalidate list queries
      queryClient.invalidateQueries({ queryKey: agentKeys.lists(tenantId) });
    },
    onError: (error) => {
      console.error('Failed to update agent:', getErrorMessage(error));
    },
  });
}

/**
 * Delete an agent
 */
export function useDeleteAgent() {
  const queryClient = useQueryClient();
  const { currentTenant } = useTenant();
  const tenantId = currentTenant?.id;
  
  return useMutation({
    mutationFn: (deletedId: string) => deleteAgent(deletedId, tenantId as string),
    onSuccess: (_, deletedId) => {
      // Remove from cache
      queryClient.removeQueries({ queryKey: agentKeys.detail(tenantId, deletedId) });
      
      // Invalidate list queries
      queryClient.invalidateQueries({ queryKey: agentKeys.lists(tenantId) });
    },
    onError: (error) => {
      console.error('Failed to delete agent:', getErrorMessage(error));
    },
  });
}

/**
 * Activate an agent (change status to active)
 */
export function useActivateAgent() {
  const queryClient = useQueryClient();
  const { currentTenant } = useTenant();
  const tenantId = currentTenant?.id;
  
  return useMutation({
    mutationFn: (id: string) => activateAgent(id, tenantId as string),
    onSuccess: (updatedAgent) => {
      queryClient.setQueryData(agentKeys.detail(tenantId, updatedAgent.id), updatedAgent);
      queryClient.invalidateQueries({ queryKey: agentKeys.lists(tenantId) });
    },
  });
}

/**
 * Archive an agent
 */
export function useArchiveAgent() {
  const queryClient = useQueryClient();
  const { currentTenant } = useTenant();
  const tenantId = currentTenant?.id;
  
  return useMutation({
    mutationFn: (id: string) => archiveAgent(id, tenantId as string),
    onSuccess: (updatedAgent) => {
      queryClient.setQueryData(agentKeys.detail(tenantId, updatedAgent.id), updatedAgent);
      queryClient.invalidateQueries({ queryKey: agentKeys.lists(tenantId) });
    },
  });
}

/**
 * Deprecate an agent (change status to deprecated)
 */
export function useDeprecateAgent() {
  const queryClient = useQueryClient();
  const { currentTenant } = useTenant();
  const tenantId = currentTenant?.id;
  
  return useMutation({
    mutationFn: (id: string) => deprecateAgent(id, tenantId as string),
    onSuccess: (updatedAgent) => {
      queryClient.setQueryData(agentKeys.detail(tenantId, updatedAgent.id), updatedAgent);
      queryClient.invalidateQueries({ queryKey: agentKeys.lists(tenantId) });
    },
  });
}

/**
 * Duplicate an agent
 */
export function useDuplicateAgent() {
  const queryClient = useQueryClient();
  const { currentTenant } = useTenant();
  const tenantId = currentTenant?.id;
  
  return useMutation({
    mutationFn: (id: string) => duplicateAgent(id, tenantId as string),
    onSuccess: (newAgent) => {
      queryClient.setQueryData(agentKeys.detail(tenantId, newAgent.id), newAgent);
      queryClient.invalidateQueries({ queryKey: agentKeys.lists(tenantId) });
    },
  });
}
